import { Map, List } from 'immutable'
import _get from 'lodash/get'
import { getBoundActions } from 'actions'
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import { connect as connectRedux } from 'react-redux'
import { getPrimary } from 'services/languages'
import { getAuthIsLoggedIn } from 'services/auth'
import { UNAVAILABLE_REASON } from 'services/share'
import { historyRedirect, HISTORY_METHOD_REPLACE } from 'services/navigation'
import { SCREEN_TYPE_SHARE_WATCH } from 'services/page-manager/entities'
import { PLAN_SKU_THREE_MONTH } from 'services/plans'
import { URL_GO_PAGE } from 'services/url/constants'
import { TYPE_TAG_RAINBOW_CATEGORY, TYPE_CONTENT_VIDEO } from 'services/content-type'
import { SHARE_OFFER_CLICKED_GA4_EVENT } from 'services/event-tracking'
import { get as getConfig } from 'config'
import { useGetPlanDataBySku } from 'services/plans/hooks'
import { isEntitled } from 'services/subscription'
import { createUpstreamContext, upstreamContextOnClick } from 'services/upstream-context'
import SharePageV3Header from './SharePage.v3Header'
import SharePageV3Meta from './SharePage.v3Meta'
import SharePageV3Video from './SharePage.v3Video'
import SharePageV3SpecialOffer, {
  OFFER_DISPLAY_TYPE_PROMO,
  OFFER_DISPLAY_TYPE_CARD,
} from './SharePage.v3SpecialOffer'
import SharePageV3Banner from './SharePage.v3Banner'
import SharePageV3Faq from './SharePage.v3Faq'
import SharePageV3VideoLimitView from './SharePage.v3VideoLimitView'
import SharePageV3RelatedMedia from './SharePage.v3RelatedMedia'

const config = getConfig()
const planGraphQLActive = !!_get(config, ['features', 'checkout', 'planGraphQL'])

const offerLink = {
  url: URL_GO_PAGE,
  query: {
    sku: PLAN_SKU_THREE_MONTH,
    utm_source: 'share',
  },
}

function SharePageV3 ({
  setDefaultGa4Event,
  setUpstreamContext,
  setQualifiedView,
  getPmScreen,
  staticText,
  pmSection,
  pmScreen,
  history,
  error,
  share,
  plans,
  auth,
  user,
  userAccount,
}) {
  const language = getPrimary(user.getIn(['data', 'language'], List()))
  const authJwt = auth.get('jwt')
  const sectionIds = pmScreen.getIn([SCREEN_TYPE_SHARE_WATCH, language, 'data', 'sectionIds'], List())
  const gqlPlanData = useGetPlanDataBySku({
    userLanguage: language,
    authJwt,
    planSkus: PLAN_SKU_THREE_MONTH,
  }) || Map()
  const formattedPlanData = gqlPlanData.get('plans', List()) || List()

  let offerPlan = plans && plans.size > 0
    ? plans.find((p) => p.get('sku') === PLAN_SKU_THREE_MONTH)
    : Map()

  if (planGraphQLActive) {
    offerPlan = List.isList(formattedPlanData)
      && formattedPlanData.size ? formattedPlanData.get(0) : Map()
  }

  const isLoggedIn = getAuthIsLoggedIn(auth)
  const isUserEntitled = isEntitled(auth.get('subscriptions', List()))
  const billingAccountId = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'billingAccountId'])
  const showRenew = isLoggedIn && !isUserEntitled && billingAccountId

  const shareId = share.get('id')
  const userReferralId = share.get('userReferralId')
  const content = share.get('content', Map())
  const unavailableReason = share.get('unavailableReason')
  const expirationDate = share.get('expirationDate')
  const expired = share.get('expired')
  const references = share.getIn(['content', 'references'], List())
  const contentUrl = share.getIn(['content', 'url'])

  const shareSpecificReason = unavailableReason && [
    UNAVAILABLE_REASON.EMAIL_VIEW_LIMIT_EXCEEDED,
    UNAVAILABLE_REASON.VIEW_LIMIT_EXCEEDED,
    UNAVAILABLE_REASON.TIME_LIMIT_EXCEEDED,
  ].includes(unavailableReason)

  const videoRef = useRef()

  const rainbowCategory = useMemo(() => {
    const tag = references.find((ref) => ref.get('reference_type') === TYPE_TAG_RAINBOW_CATEGORY) || Map()
    // first char is rainbow ID (1-9)
    return tag ? tag.get('name', '').slice(0, 1) : ''
  }, [references])

  const handleOfferClick = useCallback((e, uiElement) => {
    let eventData = {
      content_id: share.getIn(['content', 'contentId']),
      content_name: share.getIn(['content', 'title']),
      content_type: TYPE_CONTENT_VIDEO,
      ui_element: uiElement,
      ui_location: 'Share Watch',
    }
    if (share.getIn(['content', 'seriesId'])) {
      eventData = {
        ...eventData,
        parent_series_id: share.getIn(['content', 'seriesId']),
        parent_series_name: share.getIn(['content', 'seriesTitle']),
      }
    }
    setDefaultGa4Event(SHARE_OFFER_CLICKED_GA4_EVENT.mergeDeep(eventData))

    // update upstream context
    const updatedContext = createUpstreamContext({ sharePageAttribution: { click_attribution: 'Share' } })
    upstreamContextOnClick(e, { upstreamContext: updatedContext, setUpstreamContext })
  }, [share, setDefaultGa4Event])

  useEffect(() => {
    if (isLoggedIn && isUserEntitled) {
      // Entitled user is always redirected to the content page
      historyRedirect({
        url: contentUrl || '/not-found',
        language,
        history,
        auth,
        historyMethod: HISTORY_METHOD_REPLACE,
      })
    }
  }, [isLoggedIn, isUserEntitled, contentUrl])

  useEffect(() => {
    if (!isUserEntitled && !shareSpecificReason) {
      getPmScreen({ screenType: SCREEN_TYPE_SHARE_WATCH, language })
    }
  }, [isUserEntitled, shareSpecificReason, language])

  if (shareSpecificReason) {
    return (
      <section className="share-page-v3">
        <SharePageV3VideoLimitView
          staticText={staticText}
          isLoggedIn={isLoggedIn}
          unavailableReason={unavailableReason}
          showRenew={showRenew}
        />
      </section>
    )
  }

  return (
    <section className="share-page-v3">
      <SharePageV3Header shareId={shareId} userReferralId={userReferralId} />
      <div className="share-page-v3__top-content">
        <SharePageV3Video
          isAnonymous={!isLoggedIn}
          share={share}
          error={error}
          staticText={staticText}
          language={language}
          setQualifiedView={setQualifiedView}
          ref={videoRef}
        />
        {!error && !expired
          ? <div className="share-page-v3__top-content-offer">
            <SharePageV3SpecialOffer
              staticText={staticText}
              offerPlan={offerPlan}
              expirationDate={expirationDate}
              offerLink={offerLink}
              onOfferClick={(e) => handleOfferClick(e, 'top offer')}
              language={language}
            />
          </div>
          : null
        }
      </div>
      {!error && content ? (
        <SharePageV3Meta staticText={staticText} content={content} />
      ) : null}
      {sectionIds.map((sectionId, index) => {
        const section = pmSection.get(sectionId)
        if (section) {
          const key = section.getIn(['data', 'id'])
          if (key) {
            return index === 0
              ? <div key={key}>
                <SharePageV3RelatedMedia
                  section={section}
                  language={language}
                  staticText={staticText}
                  offerLink={offerLink}
                  rainbowCategory={rainbowCategory}
                  onOfferClick={(e) => handleOfferClick(e, 'middle CTA')}
                />
                <SharePageV3Banner staticText={staticText} />
              </div>
              : <SharePageV3RelatedMedia
                key={key}
                section={section}
                language={language}
                staticText={staticText}
                offerLink={offerLink}
                rainbowCategory={rainbowCategory}
                onOfferClick={(e) => handleOfferClick(e, 'middle CTA')}
              />
          }
        }
        return null
      })}
      <SharePageV3Faq staticText={staticText} offerPlan={offerPlan} language={language} />
      {!error && !expired
        ? <div className="share-page-v3__bottom-content">
          <div className="share-page-v3__bottom-content-offer--promo">
            <SharePageV3SpecialOffer
              staticText={staticText}
              offerPlan={offerPlan}
              expirationDate={expirationDate}
              offerLink={offerLink}
              displayType={OFFER_DISPLAY_TYPE_PROMO}
              onOfferClick={(e) => handleOfferClick(e, 'bottom offer')}
              language={language}
            />
          </div>
          <div className="share-page-v3__bottom-content-offer--card">
            <SharePageV3SpecialOffer
              staticText={staticText}
              offerPlan={offerPlan}
              expirationDate={expirationDate}
              offerLink={offerLink}
              displayType={OFFER_DISPLAY_TYPE_CARD}
              onOfferClick={(e) => handleOfferClick(e, 'bottom offer')}
              language={language}
            />
          </div>
        </div>
        : null
      }
    </section>
  )
}

export default connectRedux(({
  staticText,
  pmSection,
  pmScreen,
  share,
  plans,
  user,
  auth,
  userAccount,
  upstreamContext,
}) => ({
  staticText: staticText.getIn(['data', 'sharePageV3', 'data'], Map()),
  pmSection,
  pmScreen,
  share: share.get('data', Map()),
  error: share.get('success') === false,
  plans: plans.getIn(['data', 'plans']),
  auth,
  user,
  userAccount,
  upstreamContext: upstreamContext.get('data'),
}), (dispatch) => {
  const actions = getBoundActions(dispatch)
  return {
    setUpstreamContext: actions.upstreamContext.setUpstreamContext,
    setDefaultGa4Event: actions.eventTracking.setDefaultGa4Event,
    setQualifiedView: actions.share.setShareQualifiedView,
    redeemShare: actions.share.redeemShare,
    getPmScreen: actions.pmScreen.getPmScreen,
  }
})(SharePageV3)
